import axios from 'axios';
import { getSessionCookie, setSessionCookie } from '../helpers/cookies';
const api = axios.create({
    baseURL: process.env.REACT_APP_API_ENDPOINT,
})

const tenantId = window.location.host.split(':')[0].split('.')[0];

// Extrai a mensagem de erro do corpo da resposta, independente do formato
// retornado pela API (objeto com "message"/"Message" ou string pura).
const extrairMensagemErro = (data) => {
    if (data === null || data === undefined) return null;
    if (typeof data === "string") return data;
    return data.message ?? data.Message ?? null;
};

// Padroniza erro de rede/axios em { status, message } pt-BR, usado por request() e api_GET_External.
// mensagem já vem extraída de fora pois o formato do corpo do erro muda conforme o responseType (json vs blob).
const montarErro = (error, mensagem) => {
    if (error.response) {
        if (error.response.status === 401 || error.response.status === 403) {
            return { status: error.response.status, message: "Sua sessão expirou. Realize o acesso novamente" };
        }

        if (error.response.status === 413) {
            return { status: 413, message: "O arquivo enviado é muito grande. Reduza o tamanho do(s) anexo(s) e tente novamente." };
        }

        return {
            status: error.response.status,
            message: mensagem ?? "O servidor retornou um erro inesperado. Tente novamente ou contate o suporte."
        };
    }
    else if (error.code === "ECONNABORTED") {
        return { status: 408, message: "Tempo limite de conexão excedido. Verifique sua internet e tente novamente." };
    }
    else {
        return { status: 0, message: "Não foi possível se conectar ao servidor. Verifique sua conexão com a internet ou o tamanho dos arquivos anexados." };
    }
};

// Sessão expirada localmente (sem round-trip ao servidor): limpa e força novo login.
const sessaoExpirada = (session) => !!session && new Date(session.expiredToken) < new Date();

// Monta os headers padrão (tenant + auth) usados em toda chamada autenticada.
const montarHeaders = (session, contentType) => {
    const _headers = {
        'tenantId': tenantId,
        'Cache-Control': 'no-cache',
        'Pragma': 'no-cache',
        'Expires': '0',
    };

    if (contentType)
        _headers['Content-Type'] = contentType;

    if (session) {
        _headers.Authorization = `Bearer ${session.token}`;
        _headers["UserId"] = session.id;
        _headers["Secretaria"] = session.secretaria_Id;
    }

    return _headers;
}

const request = async (endpoint, obj, method, contentType) => {

    const promise = new Promise((resolve, reject) => {

        const session = getSessionCookie();

        if (sessaoExpirada(session)) {
            setSessionCookie(null);
            window.location.reload();
            resolve(null);
            return;
        }

        const success = (result => {
            var newResult = {
                status: result.status,
                message: result.message,
                data: result.data
            }

            resolve(newResult);
        });

        const failed = (error => reject(montarErro(error, extrairMensagemErro(error.response?.data))));

        const headers = () => ({ headers: montarHeaders(session, contentType) });

        // FormData vai cru: o navegador monta o multipart e o Content-Type com o boundary.
        const corpo = () => obj instanceof FormData ? obj : JSON.stringify(obj);

        try {
            if (method == "POST")
                return api.post(endpoint, corpo(), headers())
                    .then(result => success(result))
                    .catch(error => failed(error));
            else if (method == "PUT")
                return api.put(endpoint, corpo(), headers())
                    .then(result => success(result))
                    .catch(error => failed(error));
            else if (method == "GET")
                return api.get(endpoint, headers())
                    .then(result => success(result))
                    .catch(error => failed(error));
            else if (method == "DELETE")
                return api.delete(endpoint, headers())
                    .then(result => success(result))
                    .catch(error => failed(error));

        }
        catch (error) {
            var result = {
                status: 500,
                message: "Erro no servidor (500)"
            }
            reject(result);
        }
    });

    return promise;
};

const api_POST = async (endpoint, obj, contentType) => {
    let _contentType = contentType;
    if (!contentType)
        _contentType = 'application/json';
    return request(endpoint, obj, "POST", _contentType);
};

const api_PUT = async (endpoint, obj) => {
    return request(endpoint, obj, "PUT", 'application/json');
};

// Upload de arquivo: envia multipart/form-data em vez de base64 no JSON.
const api_POST_FORM = async (endpoint, formData) => {
    return request(endpoint, formData, "POST", null);
};

const api_PUT_FORM = async (endpoint, formData) => {
    return request(endpoint, formData, "PUT", null);
};

const api_DELETE = async (endpoint, obj) => {
    return request(endpoint, obj, "DELETE", 'application/json');
};

const api_GET = async (endpoint) => {
    return request(endpoint, null, "GET", 'application/json');
};

const api_GET_External = (endpoint) => {

    const promise = new Promise((resolve, reject) => {

        try {
            return axios.get(endpoint)
                .then(result => {
                    let newResult = {
                        status: result.status,
                        data: result.data,
                        headers: result.headers
                    }
                    resolve(newResult);
                })
                .catch(function (error) {
                    if (error.response) {
                        if (error.response.status === 401 || error.response.status === 403) {
                            reject({ status: error.response.status, message: "Sua sessão expirou. Realize o acesso novamente" });
                            return;
                        }

                        const mensagem = extrairMensagemErro(error.response.data);
                        reject({
                            status: error.response.status,
                            message: mensagem ?? "O servidor retornou um erro inesperado. Tente novamente ou contate o suporte."
                        });
                    }
                    else if (error.code === "ECONNABORTED") {
                        reject({ status: 408, message: "Tempo limite de conexão excedido. Verifique sua internet e tente novamente." });
                    }
                    else {
                        reject({ status: 0, message: "Não foi possível se conectar ao servidor. Verifique sua conexão com a internet." });
                    }
                });

        } catch (error) {
            let result = {
                status: 500,
                message: "Erro no servidor."
            }
            reject(result);
        }

    });

    return promise;

};

export { api_POST, api_PUT, api_POST_FORM, api_PUT_FORM, api_DELETE, api_GET, api_GET_External };